import { Component, OnInit } from '@angular/core'
import { ActivatedRoute, Params, Router } from '@angular/router'

import { AuthorLayout } from '../../../../../shared/enums/author-layout.enum'
import { FileMime } from '../../../../../shared/enums/file-mime.enum'
import { Author } from '../../../../../shared/interfaces/author.interface'
import { BreadcrumbLink } from '../../../../../shared/interfaces/breadcrumb-link.interface'
import { ImageSet } from '../../../../../shared/interfaces/image-set.interface'
import { Document } from '../../../../../shared/interfaces/resources/document.interface'
import { Post } from '../../../../../shared/interfaces/resources/post.interface'
import { WorkingGroup } from '../../../../../shared/interfaces/resources/working-group.interface'
import { TruncatePipe } from '../../../../../shared/pipes/truncate.pipe'
import { AuthorService } from '../../../../../shared/services/author.service'
import { BreadcrumbService } from '../../../../../shared/services/breadcrumb.service'
import { DocumentService } from '../../../../../shared/services/document.service'
import { MediaService } from '../../../../../shared/services/media.service'
import { MetaService } from '../../../../../shared/services/meta.service'
import { PostService } from '../../../../../shared/services/post.service'
import { WorkingGroupService } from '../../../../../shared/services/working-group.service'

@Component({
  selector: 'app-document-detail',
  templateUrl: './document-detail.component.html',
  styleUrls: ['./document-detail.component.scss']
})
export class DocumentDetailComponent implements OnInit {
  document: Document
  relatedPosts: Post[]
  mime: FileMime
  coAuthors: Author[]
  workingGroup: WorkingGroup
  authorImage: ImageSet

  breadcrumbLinks: BreadcrumbLink[]
  AuthorLayout = AuthorLayout
  FileMime = FileMime

  loading: boolean
  loadingRelatedPosts: boolean

  constructor(
    private documentService: DocumentService,
    private workingGroupService: WorkingGroupService,
    private postService: PostService,
    private authorService: AuthorService,
    private activatedRoute: ActivatedRoute,
    private mediaService: MediaService,
    private metaService: MetaService,
    private breadcrumbService: BreadcrumbService,
    private router: Router,
    private truncatePipe: TruncatePipe
  ) {}

  ngOnInit() {
    this.activatedRoute.params.subscribe((params: Params) => {
      this.loading = true

      this.documentService
        .show(params.documentSlug)
        .then(async (documentRes: Document) => {
          this.loading = false
          this.document = documentRes
          this.mime = this.setMime(this.document.path)

          if (this.document.author?.image) {
            this.authorImage = await this.mediaService.getImageSet(
              this.document.author.image
            )
          }

          if (this.document.workingGroupIds) {
            this.workingGroup = await this.workingGroupService.show(
              this.document.workingGroupIds[0]
            )
          }

          if (this.document.coAuthorIds.length) {
            this.authorService
              .listByIds(this.document.coAuthorIds)
              .then((coAuthorRes: Author[]) => {
                this.coAuthors = coAuthorRes
              })
          }

          // Get related posts (same policy area).
          if (this.document.policyAreas.length) {
            this.loadingRelatedPosts = true
            this.postService
              .listUnpaginated({
                policyAreaId: this.document.policyAreas.length.toString()
                  ? this.document.policyAreas[0].id.toString()
                  : null,
                limitTo: 3
              })
              .then((postRes: Post[]) => {
                this.loadingRelatedPosts = false
                this.relatedPosts = postRes
              })
          }

          // Meta and breadcrumbs.
          this.metaService.setTags({
            title: this.document.name,
            description: this.truncatePipe.transform(
              this.document.description,
              [160]
            ),
            path: `wonk/documents/${this.document.slug}`
          })

          this.breadcrumbLinks = this.breadcrumbService.getBreadCrumbLinks(
            'blogIndex',
            [
              {
                label: this.document.name,
                path: `wonk/documents/${this.document.slug}`
              }
            ]
          )
        })
        .catch(() => {
          this.loading = false
          this.router.navigate(['/404'])
        })
    })
  }

  setMime(path: string): FileMime {
    const extension: string = (path || '').split('.').pop()

    if (extension === 'xls' || extension === 'xlsx' || extension === 'csv') {
      return FileMime.Excel
    } else if (extension === 'pdf') {
      return FileMime.Pdf
    } else if (extension === 'ppt') {
      return FileMime.Ppt
    } else {
      return FileMime.Other
    }
  }
}
